<template>
    <v-container fluid class="pa-4" style="max-width: 1100px;">
        <v-card>
            <v-card-title class="text-h6">
                <v-icon left color="primary">mdi-calendar-clock</v-icon>
                {{ id ? 'Modifica Turno' : 'Nuovo Turno' }}
                <v-spacer></v-spacer>
                <v-btn text small @click="$router.push('/hrshifttemplates/')">
                    <v-icon left small>mdi-arrow-left</v-icon>Elenco
                </v-btn>
            </v-card-title>

            <v-card-text>
                <!-- Dati base -->
                <v-row dense>
                    <v-col cols="12" md="6">
                        <v-text-field v-model="form.name" label="Nome" prepend-icon="mdi-tag" outlined dense></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-select
                            v-model="form.type"
                            :items="[
                                { value: 'rigid', text: 'Rigido' },
                                { value: 'flexible', text: 'Flessibile' },
                                { value: 'rotating', text: 'A rotazione' },
                            ]"
                            label="Tipologia"
                            prepend-icon="mdi-clock-outline"
                            outlined
                            dense
                        ></v-select>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field v-model.number="form.in_tolerance_minutes" type="number" label="Tolleranza Ingresso (min)" prepend-icon="mdi-clock-plus" outlined dense></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field v-model.number="form.out_tolerance_minutes" type="number" label="Tolleranza Uscita (min)" prepend-icon="mdi-clock-minus" outlined dense></v-text-field>
                    </v-col>
                    <v-col cols="12" md="4">
                        <v-text-field v-model.number="form.break_minutes" type="number" label="Pausa (min)" prepend-icon="mdi-coffee" outlined dense></v-text-field>
                    </v-col>
                    <!-- Pausa minima garantita: detratta dal lavorato se non timbrata, oltre la soglia -->
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model.number="form.min_break_minutes"
                            type="number"
                            min="0"
                            clearable
                            label="Pausa minima garantita (min)"
                            prepend-icon="mdi-coffee-outline"
                            hint="Vuoto = pausa prevista dal turno (pause delle fasce + intervalli tra le fasce). 0 = nessuna detrazione."
                            persistent-hint
                            outlined
                            dense
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-text-field
                            v-model.number="form.break_threshold_minutes"
                            type="number"
                            min="0"
                            label="Pausa dovuta oltre (min lavorati)"
                            prepend-icon="mdi-timer-sand"
                            hint="Predefinito 360 (6 ore). Oltre questa soglia, se la pausa timbrata è inferiore alla minima, la differenza viene detratta senza scendere sotto la soglia."
                            persistent-hint
                            outlined
                            dense
                        ></v-text-field>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-select
                            v-model="form.rounding_rule"
                            :items="[
                                { value: 'none', text: 'Nessuno' },
                                { value: 'quarter', text: 'Al quarto d\'ora' },
                                { value: 'half', text: 'Alla mezz\'ora' },
                            ]"
                            label="Arrotondamento"
                            prepend-icon="mdi-timer-settings"
                            outlined
                            dense
                        ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-select
                            v-model="form.compensation_scope"
                            :items="[
                                { value: 'daily', text: 'Giornaliera (orario flessibile)' },
                                { value: 'weekly', text: 'Settimanale' },
                                { value: 'monthly', text: 'Mensile' },
                            ]"
                            label="Compensazione"
                            prepend-icon="mdi-calendar-range"
                            outlined
                            dense
                        ></v-select>
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-switch v-model="form.active" label="Attivo" inset color="success"></v-switch>
                    </v-col>
                </v-row>

                <v-divider class="my-3"></v-divider>

                <!-- Slot orari per giorno della settimana -->
                <div class="d-flex align-center mb-3">
                    <div class="text-subtitle-1 font-weight-bold">
                        <v-icon color="primary">mdi-calendar-week</v-icon>
                        Orario giornaliero
                    </div>
                    <v-spacer></v-spacer>
                    <v-btn small text @click="applyLunToVen">
                        <v-icon left small>mdi-content-copy</v-icon>
                        Copia Lunedì su Mar-Ven
                    </v-btn>
                </div>

                <div v-for="day in days" :key="day.value" class="mb-2">
                    <v-row dense align="center">
                        <v-col cols="12" md="2">
                            <div class="text-subtitle-2 pt-2">
                                {{ day.text }}
                            </div>
                        </v-col>
                        <v-col cols="12" md="9">
                            <div v-for="(slot, idx) in slotsByDay(day.value)" :key="slot._key" class="d-flex align-center mb-1">
                                <v-text-field
                                    v-model="slot.start_time"
                                    type="time"
                                    label="Inizio"
                                    dense
                                    outlined
                                    hide-details
                                    class="mr-2"
                                    style="max-width: 150px;"
                                ></v-text-field>
                                <v-text-field
                                    v-model="slot.end_time"
                                    type="time"
                                    label="Fine"
                                    dense
                                    outlined
                                    hide-details
                                    class="mr-2"
                                    style="max-width: 150px;"
                                ></v-text-field>
                                <v-text-field
                                    v-model.number="slot.break_minutes"
                                    type="number"
                                    label="Pausa (min)"
                                    dense
                                    outlined
                                    hide-details
                                    class="mr-2"
                                    style="max-width: 140px;"
                                ></v-text-field>
                                <v-btn icon small color="error" @click="removeSlot(slot)" title="Rimuovi">
                                    <v-icon small>mdi-delete</v-icon>
                                </v-btn>
                            </div>
                        </v-col>
                        <v-col cols="12" md="1" class="text-right">
                            <v-btn icon small color="primary" @click="addSlot(day.value)" title="Aggiungi fascia">
                                <v-icon>mdi-plus</v-icon>
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-divider></v-divider>
                </div>

                <v-divider class="my-3"></v-divider>

                <v-textarea v-model="form.notes" label="Note" prepend-icon="mdi-text" outlined dense rows="2" auto-grow></v-textarea>
            </v-card-text>

            <v-card-actions class="pb-4 px-4">
                <v-spacer></v-spacer>
                <v-btn text @click="$router.push('/hrshifttemplates/')">Annulla</v-btn>
                <v-btn color="primary" :loading="saving" :disabled="!form.name" @click="save">
                    <v-icon left small>mdi-content-save</v-icon>
                    Salva
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-container>
</template>

<script>
    export default {
        name: 'hr-shift-template-edit',

        props: {
            id: Number,
        },

        data() {
            return {
                form: this.emptyForm(),
                slots: [],
                saving: false,
                days: [
                    { value: 1, text: 'Lunedì' },
                    { value: 2, text: 'Martedì' },
                    { value: 3, text: 'Mercoledì' },
                    { value: 4, text: 'Giovedì' },
                    { value: 5, text: 'Venerdì' },
                    { value: 6, text: 'Sabato' },
                    { value: 7, text: 'Domenica' },
                ],
                slotKey: 0,
            }
        },

        methods: {
            emptyForm() {
                return {
                    name: '',
                    type: 'rigid',
                    in_tolerance_minutes: 0,
                    out_tolerance_minutes: 0,
                    break_minutes: 0,
                    // null = pausa prevista dal turno
                    min_break_minutes: null,
                    break_threshold_minutes: 360,
                    rounding_rule: 'none',
                    compensation_scope: 'daily',
                    active: true,
                    notes: '',
                }
            },
            slotsByDay(dow) {
                return this.slots.filter(s => s.day_of_week === dow)
            },
            addSlot(dow) {
                this.slots.push({
                    _key: ++this.slotKey,
                    day_of_week: dow,
                    start_time: '09:00',
                    end_time: '18:00',
                    break_minutes: 60,
                })
            },
            removeSlot(slot) {
                this.slots = this.slots.filter(s => s !== slot)
            },
            applyLunToVen() {
                const mon = this.slotsByDay(1)
                if (!mon.length) return
                // rimuovo slot di mar-ven esistenti
                this.slots = this.slots.filter(s => s.day_of_week < 2 || s.day_of_week > 5)
                // duplico Lun su Mar-Ven
                for (let d = 2; d <= 5; d++) {
                    mon.forEach(m => {
                        this.slots.push({
                            _key: ++this.slotKey,
                            day_of_week: d,
                            start_time: m.start_time,
                            end_time: m.end_time,
                            break_minutes: m.break_minutes,
                        })
                    })
                }
            },
            load() {
                if (!this.id) {
                    // Nuovo: aggiungo default per Lun-Ven
                    for (let d = 1; d <= 5; d++) this.addSlot(d)
                    return
                }
                this.axios.get(`hrshifttemplates/${this.id}/edit`).then(r => {
                    const m = r.data.model
                    if (m) {
                        Object.keys(this.form).forEach(k => {
                            if (m[k] !== undefined) this.form[k] = m[k]
                        })
                    }
                    const slots = r.data.slots || []
                    this.slots = slots.map(s => ({
                        _key: ++this.slotKey,
                        day_of_week: parseInt(s.day_of_week),
                        start_time: this.normalizeTime(s.start_time),
                        end_time: this.normalizeTime(s.end_time),
                        break_minutes: parseInt(s.break_minutes || 0),
                    }))
                })
            },
            normalizeTime(t) {
                if (!t) return ''
                // Se formato "HH:MM:SS", tronca a "HH:MM"
                return t.substring(0, 5)
            },
            save() {
                this.saving = true
                const payload = Object.assign({}, this.form, {
                    slots: this.slots.map(s => ({
                        day_of_week: s.day_of_week,
                        start_time: s.start_time,
                        end_time: s.end_time,
                        break_minutes: s.break_minutes || 0,
                    })),
                })
                const method = this.id ? 'put' : 'post'
                const url = this.id ? `hrshifttemplates/${this.id}` : 'hrshifttemplates'
                this.axios[method](url, payload).then(
                    () => {
                        this.saving = false
                        this.$router.push('/hrshifttemplates/')
                    },
                    () => { this.saving = false }
                )
            },
        },

        created() {
            this.$store.commit('endProgress')
            this.load()
        },
    }
</script>
